package ai.beakman.undercover.i18n

/** UI texts in English (en). */
object StringsEn : Strings {
    // --- App / branding / splash ---
    override val appTitle = "UNDERCOVER"
    override val webBrandTitle = "UNDERCOVER"
    override val webBrandTagline = "Who are you?"
    override val splashContentDescription = "Undercover"
    override val webHomeHeroAlt = "Undercover"

    // --- Home: escolha de modo ---
    override val homeHowToPlay = "How will you play?"
    override val modeLocalTitle = "Local"
    override val modeLocalSubtitle = "Pass one phone around the room."
    override val modeOnlineTitle = "Online"
    override val modeOnlineSubtitleShared = "Everyone on their own phone, with a code."
    override val modeOnlineSubtitleWeb = "Everyone on their own device, with a code."
    override val onlineSoonTitle = "Online mode"
    override val onlineSoonBody = "Coming in the next phase (Firestore already set up)."

    // --- Navegação / voltar / sair ---
    override val backVoltar = "Back"
    override val backTrocarModo = "Change mode"
    override val backArrowVoltar = "← Back"
    override val backArrowSair = "← Leave"
    override val exitSair = "✕ Leave"

    // --- Setup local: jogadores ---
    override fun playersCount(n: Int) = "Players ($n)"
    override val namesAddHint = "Add everyone playing on this phone."
    override val namesPlayerPlaceholder = "Player name"
    override fun minPlayers(n: Int) = "At least $n players."
    override val continueNext = "Continue →"

    // --- Setup: tema ---
    override val chooseTheme = "Pick a theme"
    override val themeWillBeDrawn = "A word from this theme will be drawn."

    // --- Setup: infiltrados ---
    override val infiltratorsTitle = "Undercover"
    override val infiltratorSingular = "undercover"
    override val infiltratorPlural = "undercover"
    override val needTwoCommons = "You need at least 2 regular players."

    // --- Setup: tempo / iniciar ---
    override val discussionTimeTitle = "Discussion time"
    override val startMatch = "Start game"

    // --- Reveal ---
    override fun revealPlayerXofN(i: Int, n: Int) = "Player $i of $n"
    override val youAre = "YOU ARE"
    override val infiltrator = "UNDERCOVER"
    override val hintLabel = "Hint:"
    override val yourAccompliceSingular = "Your accomplice:"
    override val yourAccomplicesPlural = "Your accomplices:"
    override val yourWord = "YOUR WORD"
    override fun themeWithEmoji(emoji: String, name: String) = "Theme: $emoji $name"
    override val revealDragHint = "Swipe up and hold\nto see your card"
    override val revealHoldHint = "Hold to see your card"
    override val nextPlayer = "Next player →"
    override val startDiscussion = "Start discussion →"

    // --- Discussão / timer ---
    override val discussionPrompt = "Ask each other questions. Who's the undercover?"
    override val goToVote = "Go to vote"

    // --- Votação ---
    override val voteTitle = "Voting"
    override fun voteTurnOf(name: String) = "$name's turn"
    override fun voteChooseSuspectsLocal(n: Int) = "Pick $n suspect(s) (can't vote for yourself)"
    override val voteWhoIsInfiltrator = "Who's the undercover?"
    override fun voteChoosePlayersOnline(n: Int) = "Pick $n player(s)"
    override val voteRegisteredWaitingShared = "Vote cast. Waiting for the others."
    override fun voteRegisteredWaitingWeb(voted: Int, total: Int) =
        "Vote cast. Waiting for the others... ($voted/$total voted)"
    override fun voteSelectedOfNParen(selected: Int, n: Int) = "$selected/$n selected"
    override fun voteSelectedOfNSuffix(selected: Int, n: Int) = "$selected/$n selected"
    override val voteConfirm = "Confirm vote"
    override val voteSending = "Sending..."
    override val voteConfirmArrow = "Confirm vote →"
    override val voteConfirmAndResult = "Confirm and see result"
    override val voteStatusVoted = "voted"
    override val voteStatusVoting = "voting..."
    override val showResult = "Show result"
    override fun showResultPartial(voted: Int, total: Int) = "Show result ($voted/$total voted)"
    override val seeResult = "See result"
    override fun seeResultPartial(voted: Int, total: Int) = "See result ($voted/$total voted)"

    // --- Resultado ---
    override val resultPlayersWonEmoji = "🎉"
    override val resultInfiltratorWonEmoji = "🕵️"
    override val resultPlayersWon = "Players win!"
    override val resultInfiltratorWon = "Undercover wins!"
    override val resultCaughtAll = "You caught the undercover."
    override val resultCaughtSome = "You caught one, but not all."
    override val resultCaughtNone = "The undercover fooled everyone."
    override val resultWordWas = "The word was"
    override val resultInfiltratorSingular = "Undercover"
    override val resultInfiltratorsPlural = "Undercover"
    override val resultVotesLabel = "Votes"
    override fun votesCountLocal(count: Int) = "$count vote(s)"
    override fun votesCountOnline(count: Int) = "$count vote(s)"
    override val playAgain = "Play again"
    override val configureNewGame = "Set up a new game"

    // --- Online: menu / criar / entrar ---
    override val onlineModeLabel = "Online Mode"
    override val onlineMenuSubtitle = "Everyone on their own phone, same room."
    override val createRoomCardTitle = "Create room"
    override val createRoomCardSubtitle = "You become the host"
    override val joinRoomCardTitle = "Join with a code"
    override val joinRoomCardSubtitle = "Hop into a room"
    override val createRoomDesc = "Pick your name and the game's theme."
    override val yourNamePlaceholder = "Your name"
    override val themeLabel = "Theme"
    override val createRoomCreating = "Creating..."
    override val createRoomError =
        "Couldn't create the room. Check your connection and try again."
    override val joinRoomDesc = "Ask the host for the 5-letter code."
    override val codePlaceholder = "CODE"
    override val joinEnter = "Join"
    override val joinEntering = "Joining..."
    override fun joinRoomNotFound(code: String) =
        "Room $code not found. Double-check the code with the host."
    override val joinRoomError =
        "Couldn't join the room. Check your connection and try again."

    // --- Online: erros / estado de sala ---
    override val actionSyncError =
        "Couldn't sync with the room. Check your connection and try again."
    override val roomClosed = "The room was closed."
    override val loadingRoom = "Loading room..."
    override val roomLabel = "Room"
    override val roomCodeLabel = "Room code"

    // --- Online: convite / cópia ---
    override val inviteButton = "📤 Invite"
    override val inviteCopiedConfirm = "✓ Invite copied!"
    override val copyCode = "Copy code"
    override val copiedShared = "Copied!"
    override val copiedWeb = "✓ Copied!"
    override fun inviteMessage(code: String, baseUrl: String) =
        "Come play Undercover with me! 🕵️\nJoin the room: $baseUrl/sala/$code\nRoom code: $code"

    // --- Online: lobby ---
    override val timeLabel = "Time"
    override val hostBadgeShared = "HOST"
    override val hostBadgeWeb = "⭐ HOST"
    override val playerYouSuffix = "(you)"
    override val lobbyStartWeb = "Start"
    override val lobbyStartingWeb = "Starting..."
    override val waitingPlayersMin3 = "Waiting for players (min. 3)"
    override val waitingHostStartShared = "Waiting for the host to start the game..."
    override val waitingHostStartWeb = "Waiting for the host to start..."

    // --- Online: reveal ---
    override fun revealTopBarReady(ready: Int, total: Int) = "Your card · $ready/$total ready"
    override val imReady = "I'm ready"
    override val readyConfirmed = "✓ Ready"
    override fun waitingNReady(remaining: Int) = "Waiting for $remaining..."
    override val revealSecretCardCover = "SECRET CARD"
    override val revealHoldToPeek = "Hold to peek"
    override val revealOnlyYouCanSee = "Only you can see this. Release to hide."

    // --- Online: resultado ---
    override val resultLeaveRoom = "Leave room"
    override val resultPreparing = "Preparing..."
}
